(ns assemble.resource.template
  (:require [assemble.component.upload-modal :as upload-modal]
            [assemble.templates-page.upload-modal.db :as upload-modal.db]
            [camel-snake-kebab.core :as csk]
            [camel-snake-kebab.extras :as cske]
            [common.db.asset-upload.resumable :as asset-upload.resumable]
            [common.db.effects.http :refer [jsonify]]
            [common.url :as url]
            [common.url-template :as url-tpl]
            [linked.core :as l]
            [re-frame.core :as rf]))

(def entity-name "My Templates")

(def pre-made-template-folder-id 99999)

(defn get-latest-revision
  [template]
  (apply max-key :version (:revisions template)))

(defn get-request-params-template-audit
  ([advertiser-id folder-id include-orphaned-templates] (get-request-params-template-audit advertiser-id folder-id include-orphaned-templates nil nil nil))
  ([advertiser-id folder-id include-orphaned-templates page search sort-direction]
   (cond-> {:advertiser-id advertiser-id
            :folder-id folder-id
            :include-orphaned include-orphaned-templates
            :embed (jsonify ["revisions"])}
     page (merge {:page (+ 1 (/ page 10))
                  :pagesize 10})
     sort-direction (merge {:sort
                            (str (when (= (:direction sort-direction) :desc) "-")
                                 (name (:column sort-direction)))})
     (seq (:term search)) (merge {:filters
                                  (jsonify [["has"
                                             (:column search)
                                             (:term search)]])}))))

(defn response->manage-api-format
  "Transforms the response data's keys into camelCased format,
   as used by the python manage-api, except for the keys in the placeholders."
  [response]
  (letfn [(transform-data [data] (mapv transform-template data))
          (transform-revision [revision] (merge (cske/transform-keys csk/->camelCase revision)
                                                (select-keys revision [:placeholders])))
          (transform-template [template] (merge (cske/transform-keys csk/->camelCase template)
                                                {:revisions (->> template :revisions (mapv transform-revision))}))]
    (if (map? response)
      (-> (cske/transform-keys csk/->camelCase response)
          (assoc :data (transform-data (:data response))))
      (transform-data response))))

(defn prepare-single-template-response [response-json]
  (let [response (js->clj (js/JSON.parse response-json))
        revisions (mapv (fn [{:strs [placeholders] :as revision}]
                          (-> (cske/transform-keys csk/->camelCaseKeyword revision)
                              (assoc :placeholders placeholders)))
                        (get response "revisions"))]
    (-> (cske/transform-keys csk/->camelCaseKeyword response)
        (assoc :latestRevision (first (sort-by :updatedAt > revisions))))))

(rf/reg-event-fx ::get-all-by-node
  [(rf/inject-cofx :sub :resource.user/token)
   (rf/inject-cofx :sub :config)
   (rf/inject-cofx :sub :context)]
  (fn [{:keys [db config context] :as cofx} [_ {:asset-tree/keys [folder-type] node-id :asset-tree/id}]]
    (let [include-orphaned-templates (= folder-type "templates")]
      {:db (update-in db [::templates-by-node node-id] merge {:state :pending})
       :http
       {:method :get
        :uri (url/parts->str [(:apiUrl config) "advertiser-api/v1/templates"])
        :params (get-request-params-template-audit (:advertiser-id context) node-id include-orphaned-templates)
        :headers {"Authorization" (:resource.user/token cofx)}
        :on-success [::get-all-by-node-success node-id]
        :on-failure [::get-all-by-node-failure node-id]}})))

(rf/reg-event-fx ::get-public-pre-made-templates
  [(rf/inject-cofx :sub :resource.user/token)
   (rf/inject-cofx :sub :config)]
  (fn [{:keys [db config] :as cofx} [_ node-id]]
    {:db (update-in db [::templates-by-node node-id] merge {:state :pending})
     :http
     {:method     :get
      :uri        (url/parts->str [(:apiUrl config) "advertiser-api/v1/pre-made-templates"])
      :headers    {"Authorization" (:resource.user/token cofx)}
      :on-success [::get-all-by-node-success-pre-made node-id]
      :on-failure [::get-all-by-node-failure node-id]}}))

(rf/reg-event-fx ::get-all-by-node-success-pre-made
  (fn [{:keys [db]} [_ _node-id response-data]]
    (let [templates response-data
          result (mapv #(assoc % :latestRevision (get-latest-revision %) :pre-made-template true) templates)
          result* (mapv #(assoc % :folderId pre-made-template-folder-id) result)]
      {:db (assoc-in db [::templates-by-node pre-made-template-folder-id] {:state :success
                                                                           :value (into (l/map) (map (juxt :id identity) result*))})})))

(rf/reg-event-fx ::get-all-by-node-success
  (fn [{:keys [db]} [_ node-id response-data]]
    (let [templates (:data (response->manage-api-format response-data))
          result (mapv #(assoc % :latestRevision (get-latest-revision %)) templates)]
      {:db (assoc-in db [::templates-by-node node-id] {:state :success
                                                       :value (into (l/map) (map (juxt :id identity) result))})})))

(rf/reg-event-db ::get-all-by-node-failure
  (fn [db [_ [_ node-id]]]
    (assoc-in db [::templates-by-node node-id] {:state :failure
                                                :value {}})))

;; Dispatching this as an rf event would cause the old value flash due to
;; async event loop.
(defn update-template-query-result [db folder-node-id template]
  (update-in db [::templates-by-node folder-node-id :value (:id template)] merge template))

(rf/reg-event-db ::upload-progress
  (fn [db [_ queue-id progress]]
    (assoc-in db [:templates-upload :queue queue-id :progress] progress)))

(defn create-html-fx [node-id selected-template queue-item is-premade-template?]
  (let [{queue-id :id template-file :file :keys [template-type]} queue-item
        ;; selected-template comes from the Autocomplete in the upload modal
        {template-name :name template-id :id} selected-template
        payload {:name template-name :templateType template-type :template template-file :archived is-premade-template?}]
    (if-not template-id
      [:dispatch [::post node-id queue-id payload]]
      [:dispatch [::post-revision queue-id template-id payload]])))

(defn create-after-effects-fx [url-context node-id selected-template queue-item is-premade-template? pre-made-template-id]
  (let [{queue-id :id template-file :file} queue-item
        {template-name :name template-id :id} selected-template
        payload (cond-> {:name template-name
                         :folder-id node-id
                         :archived is-premade-template?
                         :labels []}
                  pre-made-template-id (assoc :pre-made-template-id pre-made-template-id))]
    [::start-after-effects-upload
     {:uri (url-tpl/render :api/template-upload.resumable url-context)
      :path [:template]
      :on-progress [::upload-progress queue-id]
      :on-failure [::post-failure queue-id]
      :on-change (fn [url]
                   (rf/dispatch [::ae-post
                                 queue-id
                                 url-context
                                 template-id
                                 (assoc payload :base-url url)]))
      :file template-file
      :public false
      :unzip true}]))

;; If no upload is in progress, takes the first queued file, and starts uploading it.
;; Once upload is finished, or throws an error, the files status is updated and
;; the callback triggers this event again.
(rf/reg-event-fx ::start-upload
  [(rf/inject-cofx :sub :url-context)
   (rf/inject-cofx :sub :assemble.page.assets.file-tree/current-node-id)
   (rf/inject-cofx :sub :premade-templates/is-premade-templates-advertiser?)]
  (fn [{:keys [db url-context] :as cofx} _]
    (let [files (:queue (:templates-upload db))
          active-item (upload-modal/queue-first-by-status files "uploading")
          {:keys [template-type] :as first-item} (upload-modal/queue-first-by-status files "queue")]
      (when (and (not active-item) first-item)
        (let [selected-template (upload-modal.db/get-selected-template db)
              pre-made-template-id (upload-modal.db/get-pre-made-template-id db)
              node-id (:assemble.page.assets.file-tree/current-node-id cofx)
              is-premade-template? (:premade-templates/is-premade-templates-advertiser? cofx)
              upload-fx (case template-type
                          "html" (create-html-fx node-id selected-template first-item is-premade-template?)
                          "after-effects" (create-after-effects-fx url-context node-id selected-template first-item is-premade-template? pre-made-template-id))]
          ;; Modal is kept open when the uploads are done,
          ;; so the user sees all the status for the uploads.
          {:db (assoc-in db [:templates-upload :queue (:id first-item) :status] "uploading")
           :fx [upload-fx]})))))

(rf/reg-fx ::start-after-effects-upload
  (fn [props]
    (asset-upload.resumable/start-upload props)))

;; This function exists to allow us to monitor upload progress
(defn upload [{:keys [url queue-id payload token]}]
  (js/Promise. (fn [resolve reject]
                 (let [req (js/XMLHttpRequest.)]
                   (.addEventListener req "load"
                                      (fn [_]
                                        (if (<= 200 (.-status req) 299)
                                          (resolve (.-response req))
                                          (reject (.-response req)))))
                   (.addEventListener (.-upload req) "progress"
                                      (fn [e]
                                        (when (.-lengthComputable e)
                                          (let [pct (* 100 (/ (.-loaded e)
                                                              (.-total e)))]
                                            (rf/dispatch [::upload-progress queue-id pct])))))
                   (.addEventListener req "error" (fn [e] (reject e)))
                   (.open req "POST" url)
                   ;; setRequestHeader must be after open
                   (.setRequestHeader req "Authorization" token)
                   (.send req payload)))))

(rf/reg-event-fx ::post
  [(rf/inject-cofx :sub :resource.user/token)
   (rf/inject-cofx :sub :config)
   (rf/inject-cofx :sub :context)]
  (fn [{:keys [config context] :as cofx} [_ current-folder-id queue-id {:keys [template] :as payload}]]
    (let [token (:resource.user/token cofx)]
      (-> (upload {:queue-id queue-id
                   :url (url/parts->str [(:apiUrl config) "api" "v0/templates"])
                   :token token
                   :payload (doto (js/FormData.)
                              (.append "json" (-> payload
                                                  (dissoc :template)
                                                  (assoc :advertiserId (-> context
                                                                           :advertiser-id
                                                                           js/parseInt))
                                                  (assoc :folderId current-folder-id)
                                                  clj->js
                                                  js/JSON.stringify))
                              (.append "template" template))})
          (.then (fn [response]
                   (let [response* (prepare-single-template-response response)]
                     (rf/dispatch [::post-success queue-id response*]))))
          (.catch (fn [response]
                    (rf/dispatch [::post-failure queue-id response])))))
    {}))

(rf/reg-event-fx ::post-success
  [(rf/inject-cofx :sub :assemble.page.assets.file-tree/current-node-id)]
  (fn [{:keys [db] :as cofx} [_ queue-id response]]
    (let [current-node-id (:assemble.page.assets.file-tree/current-node-id cofx)]
      {:db (-> db
               (update-template-query-result current-node-id response)
               (assoc-in [:templates-upload :queue queue-id :status] "done"))
       :fx [[:dispatch [::start-upload]]
            [:dispatch [:response-handler/handle-response
                        {:entity-name entity-name
                         :type :success
                         :message "Template uploaded"}]]]})))

(rf/reg-event-fx ::post-failure
  (fn [{:keys [db]} [event queue-id response]]
    {:db (assoc-in db [:templates-upload :queue queue-id :status] "error")
     :fx [[:dispatch [::start-upload]]
          [:dispatch [:response-handler/handle-response
                      {:entity-name entity-name
                       :type :error
                       :message "Template upload failed"
                       :event event
                       :response response}]]]}))

(rf/reg-event-fx
  ::download-ae-template
  [(rf/inject-cofx :sub :resource.user/token)
   (rf/inject-cofx :sub :url-context)]
  (fn [{:keys [url-context] :resource.user/keys [token]} [_ on-success on-failure base-url name]]
    {:fx [[:dispatch [:loader/open]]]
     :http
     {:method :post
      :uri (url-tpl/render :api/template-upload.download url-context)
      :headers {"Authorization" token}
      :params {:base-url base-url
               :name name}
      :response-format :binary+filename
      :on-success on-success
      :on-failure on-failure}}))

(rf/reg-event-fx
  ::download-display-template
  [(rf/inject-cofx :sub :resource.user/token)
   (rf/inject-cofx :sub :config)]
  (fn [{:keys [config] :resource.user/keys [token]} [_ on-success on-failure template-id creation-tool]]
    {:fx [[:dispatch [:loader/open]]]
     :http
     {:method :post
      :uri (url/parts->str [(:apiUrl config) "api" "v0/templates/download"])
      :params {:template-id template-id
               :creation-tool creation-tool}
      :headers {"Authorization" token}
      :response-format :binary+filename
      :on-success on-success
      :on-failure on-failure}}))

;; Just change the status back from error to queue, and try
;; starting the upload again. If there is a uploading already
;; in progress, start-upload will be no-op, and this item will
;; be started after the previous upload finishes.
(rf/reg-event-fx ::retry-upload
  (fn [{:keys [db]} [_ id]]
    {:db (update-in db [:templates-upload :queue id :status] (fn [status]
                                                               ;; Only allow error -> queue transition,
                                                               ;; the button should only be visible on this case, but
                                                               ;; to be extra careful to not break the state.
                                                               (if (= "error" status)
                                                                 "queue"
                                                                 status)))
     :fx [[:dispatch [::start-upload]]]}))

(rf/reg-event-fx ::patch
  [(rf/inject-cofx :sub :resource.user/token)
   (rf/inject-cofx :sub :config)]
  (fn [{:keys [config] :resource.user/keys [token]} [_ on-success on-failure template-id payload]]
    {:http
     {:method :patch
      :uri (url/parts->str [(:apiUrl config) "api" "v0/templates" template-id])
      :params payload
      :headers {"Authorization" token}
      :on-success on-success
      :on-failure on-failure}}))

(rf/reg-event-fx ::template-patch-success
  (fn [{:keys [db]} [_ folder-node-id resolve response]]
    ;; Use the PATCH request response to update the list in app-db
    ;; NOTE: The response is missing :latestRevision and :revisions keys,
    ;; but we merge the document with the existing data so we keep those from the
    ;; list response. This is OK for rename and archive operations.
    {:db (update-template-query-result db folder-node-id (dissoc response :revisionIds))
     :fx [[:promise/resolve resolve]
          [:dispatch [:toaster-bus/add {:id ::template-patch-success
                                        :type :success
                                        :message "Template changes saved"}]]]}))

(rf/reg-event-fx ::template-patch-failure
  (fn [_ [event reject response]]
    {:fx [[:dispatch [:toaster-bus/add {:id ::template-patch-failure
                                        :type :error
                                        :message "Something went wrong while saving the template changes."
                                        :event event
                                        :response response}]]
          [:promise/reject reject]]}))

(rf/reg-event-fx ::archive-template
  [(rf/inject-cofx :sub :assemble.page.assets.file-tree/current-node-id)]
  (fn [{folder-node-id :assemble.page.assets.file-tree/current-node-id} [_ template-id archived is-pre-made-templates-advertiser?]]
    {:fx [[:dispatch [::patch
                      [::template-archive-success folder-node-id archived is-pre-made-templates-advertiser?]
                      [::template-archive-failure archived is-pre-made-templates-advertiser?]
                      template-id {:archived archived}]]]}))

(rf/reg-event-fx ::template-archive-success
  (fn [{:keys [db]} [_ folder-node-id archived is-pre-made-templates-advertiser? response]]
    {:db (update-template-query-result db folder-node-id (dissoc response :revisionIds))
     :fx [[:dispatch [:toaster-bus/add {:id ::template-patch-success
                                        :type :success
                                        :message
                                        (if is-pre-made-templates-advertiser?
                                          (str "The template is "
                                               (if archived
                                                 "hidden"
                                                 "successfully made public and available")
                                               " for all advertisers")
                                          (str "The template has been successfully " (if archived "archived" "unarchived")))}]]]}))

(rf/reg-event-fx ::template-archive-failure
  (fn [_ [event archived is-pre-made-templates-advertiser? response]]
    {:fx [[:dispatch [:toaster-bus/add {:id ::template-patch-failure
                                        :type :error
                                        :message (if is-pre-made-templates-advertiser?
                                                   (str "Something went wrong while "
                                                        (if archived "hiding from public" "making public")
                                                        " the template.")
                                                   (str "Something went wrong while "
                                                        (if archived "archiving" "unarchiving") "
                                                         the template changes."))
                                        :event event
                                        :response response}]]]}))

(rf/reg-event-fx ::ae-post
  [(rf/inject-cofx :sub :resource.user/token)]
  (fn [{:resource.user/keys [token]} [_ queue-id url-context template-id payload]]
    (if (nil? template-id)
      ;; Post a new template
      {:http {:method :post
              :uri (url-tpl/render :api/advertiser-templates url-context)
              :params {:advertiser-id (:advertiser-id url-context)
                       :template-type "after-effects"
                       :name (:name payload)
                       :labels (:labels payload)
                       :base-url (:base-url payload)
                       :archived (:archived payload)
                       :pre-made-template-id (:pre-made-template-id payload)
                       :folder-id (:folder-id payload)}
              :headers {"Authorization" token}
              :on-success [::ae-post-success queue-id]
              :on-failure [::ae-post-failure queue-id]}}
      ;; Post a new revision of the template
      {:http {:method :post
              :uri (url-tpl/render :api/advertiser-template-revision (assoc url-context :template-id template-id))
              :params {:advertiser-id (:advertiser-id url-context)
                       :name (:name payload)
                       :labels (:labels payload)
                       :base-url (if (contains? payload :base-url)
                                   (:base-url payload)
                                   (-> payload :latestRevision :baseUrl))}
              :headers {"Authorization" token}
              :on-success [::ae-post-success queue-id]
              :on-failure [::ae-post-failure queue-id]}})))

(rf/reg-event-fx ::ae-post-success
  [(rf/inject-cofx :sub :assemble.page.assets.file-tree/current-node)]
  (fn [{:keys [db] :as cofx} [_ queue-id]]
    (let [current-node (:assemble.page.assets.file-tree/current-node cofx)]
      {:db (assoc-in db [:templates-upload :queue queue-id :status] "done")
       :fx [[:dispatch [::get-all-by-node current-node]]
            [:dispatch [::start-upload]]
            [:dispatch [:response-handler/handle-response
                        {:entity-name entity-name
                         :type :success
                         :message "Template uploaded"}]]]})))

(rf/reg-event-fx ::ae-post-failure
  (fn [{:keys [db]} [event queue-id response]]
    {:db (assoc-in db [:templates-upload :queue queue-id :status] "error")
     :fx [[:dispatch [::start-upload]]
          [:dispatch [:response-handler/handle-response
                      {:entity-name entity-name
                       :type :error
                       :message "Template upload failed"
                       :event event
                       :response response}]]]}))

(rf/reg-event-fx ::delete
  [(rf/inject-cofx :sub :resource.user/token)
   (rf/inject-cofx :sub :config)]
  (fn [{:keys [config] :resource.user/keys [token]} [_ template-id]]
    {:http
     {:method :delete
      :uri (url/parts->str [(:apiUrl config) "api" "v0/templates" template-id])
      :headers {"Authorization" token}
      :on-success [::delete-success]
      :on-failure [::delete-failure]}}))

(rf/reg-event-fx ::delete-success
  [(rf/inject-cofx :sub :assemble.page.assets.file-tree/current-node)]
  (fn [cofx [_]]
    (let [current-node (:assemble.page.assets.file-tree/current-node cofx)]
      {:fx [[:dispatch [::get-all-by-node current-node]]
            [:dispatch [:response-handler/handle-response
                        {:entity-name entity-name
                         :type :success
                         :message "Template deleted"}]]]})))

(rf/reg-event-fx ::delete-failure
  (fn [_ [event response]]
    {:fx [[:dispatch [:response-handler/handle-response
                      {:entity-name entity-name
                       :type :error
                       :message "Failed to delete template"
                       :event event
                       :response response}]]]}))

(rf/reg-event-fx ::post-revision
  [(rf/inject-cofx :sub :resource.user/token)
   (rf/inject-cofx :sub :config)]
  (fn [{:keys [config] :resource.user/keys [token]} [_ queue-id template-id template]]
    (-> (upload {:queue-id queue-id
                 :url (url/parts->str [(:apiUrl config) "api" "v0/templates" template-id "revisions"])
                 :token token
                 :payload (doto (js/FormData.)
                            (.append "template" (:template template)))})
        (.then (fn [_]
                 (rf/dispatch [::post-revision-success queue-id])))
        (.catch (fn [response]
                  (rf/dispatch [::post-revision-failure queue-id response]))))
    {}))

(rf/reg-event-fx ::post-revision-success
  [(rf/inject-cofx :sub :assemble.page.assets.file-tree/current-node)]
  (fn [{:keys [db] :as cofx} [_ queue-id]]
    (let [current-node (:assemble.page.assets.file-tree/current-node cofx)]
      {:db (assoc-in db [:templates-upload :queue queue-id :status] "done")
       :fx [[:dispatch [::get-all-by-node current-node]]
            [:dispatch [::start-upload]]
            [:dispatch [:response-handler/handle-response
                        {:entity-name entity-name
                         :type :success
                         :message "Template revision uploaded"}]]]})))

(rf/reg-event-fx ::post-revision-failure
  (fn [{:keys [db]} [event queue-id response]]
    {:db (assoc-in db [:templates-upload :queue queue-id :status] "error")
     :fx [[:dispatch [::start-upload]]
          [:dispatch [:response-handler/handle-response
                      {:entity-name entity-name
                       :type :error
                       :message "Template revision upload failed"
                       :event event
                       :response response}]]]}))

(rf/reg-sub ::templates-state
  (fn [db [_ node-id]]
    (get-in db [::templates-by-node node-id :state] [])))

(rf/reg-sub ::templates-by-node
  (fn [db [_ node-id]]
    (get-in db [::templates-by-node node-id :value] [])))

(rf/reg-sub ::disable-feature-for-pre-made-templates?
  :<- [:router/current-route]
  (fn [route _]
    (let [route-id (get-in route [:parameters :query :node])]
      (not= route-id pre-made-template-folder-id))))
